import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Row, Col, InputGroup, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { LoadingState } from '../../components/common/LoadingState';
import { useTranslation } from 'react-i18next';
import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import axiosInstance from '../../api/axios';

function Gallery() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingSrc, setDeletingSrc] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/catalog/gallery');
      if (response.data.success) {
        setPhotos(response.data.data.map((img) => ({
          ...img,
          originalSrc: img.src,
        })));
      }
    } catch (err) {
      console.error('Error cargando galería:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images[]', files[i]);
    }

    try {
      const response = await axiosInstance.post('/admin/catalog/gallery/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message || t('gallery_images_uploaded', 'Imágenes subidas correctamente.'));
        fetchGallery();
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(err.response?.data?.message || t('error_uploading_images', 'Error subiendo imágenes al servidor.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (src, e) => {
    e.stopPropagation();
    if (!window.confirm(t('confirm_delete_image_server', '¿Seguro que deseas eliminar permanentemente esta imagen del servidor?'))) return;
    
    setDeletingSrc(src);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const response = await axiosInstance.delete('/admin/catalog/gallery/delete', {
        data: { src: src }
      });
      if (response.data.success) {
        setSuccessMessage(response.data.message || t('gallery_image_deleted', 'Imagen eliminada exitosamente.'));
        fetchGallery();
      }
    } catch (err) {
      const msg = err.response?.data?.message || t('error_deleting_image', 'Error eliminando la imagen.');
      setErrorMessage(msg);
    } finally {
      setDeletingSrc(null);
    }
  };

  const openLightBox = (idx) => {
    setIndex(idx);
    setIsOpen(true);
  };

  const filteredPhotos = photos.filter(p => {
    if (!search) return true;
    const name = (p.originalSrc || p.src || '').toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <>
      {/* Page Header */}
      <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h3 className="page-title mb-1" style={{ color: '#00024f', fontWeight: 'bold' }}>
            <i className="mdi mdi-image-multiple text-primary me-2"></i>
            {t('gallery_title', 'Gestor de Galería Multimedia')}
          </h3>
          <p className="text-muted small mb-0">
            {t('gallery_subtitle', 'Imágenes centrales almacenadas en el servidor Rutex y sincronizadas con la App y el Backoffice.')}
          </p>
        </div>
        <div>
          <input 
            type="file" 
            multiple 
            accept="image/png, image/jpeg, image/webp" 
            style={{ display: 'none' }} 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
          />
          <Button 
            style={{ backgroundColor: '#183eeb', borderColor: '#183eeb', color: 'white', borderRadius: '10px', padding: '10px 22px', fontWeight: 'bold' }} 
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading || loading}
            className="shadow-sm"
          >
            {uploading ? (
              <><Spinner size="sm" animation="border" className="me-2" /> {t('uploading_images', 'Subiendo imágenes...')}</>
            ) : (
              <><i className="mdi mdi-cloud-upload me-2"></i> {t('btn_upload_image', 'Subir Nuevas Imágenes')}</>
            )}
          </Button>
        </div>
      </div>

      {/* Notifications / Alerts */}
      {errorMessage && (
        <Alert 
          variant="danger" 
          dismissible 
          onClose={() => setErrorMessage('')} 
          className="shadow-sm mb-4" 
          style={{ borderRadius: '10px', borderLeft: '5px solid #dc2626' }}
        >
          <div className="d-flex align-items-center">
            <i className="mdi mdi-alert-circle fs-4 me-2"></i>
            <div>{errorMessage}</div>
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert 
          variant="success" 
          dismissible 
          onClose={() => setSuccessMessage('')} 
          className="shadow-sm mb-4" 
          style={{ borderRadius: '10px', borderLeft: '5px solid #10b981' }}
        >
          <div className="d-flex align-items-center">
            <i className="mdi mdi-check-circle fs-4 me-2"></i>
            <div>{successMessage}</div>
          </div>
        </Alert>
      )}

      {/* Main Content Card */}
      <div className="row grid-margin">
        <div className="col-lg-12">
          <Card className="shadow-sm border-0" style={{ borderRadius: '14px', backgroundColor: '#ffffff' }}>
            <Card.Body className="p-4">
              
              {/* Search and stats bar */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom gap-2">
                <div style={{ maxWidth: '380px', width: '100%' }}>
                  <div className="position-relative">
                    <i 
                      className="mdi mdi-magnify position-absolute text-muted" 
                      style={{ left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.15rem', zIndex: 5, pointerEvents: 'none' }}
                    />
                    <Form.Control
                      type="text"
                      placeholder={t('search_gallery', 'Buscar imágenes por nombre de archivo o SKU...')}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="w-100 shadow-none"
                      style={{
                        paddingLeft: '40px',
                        paddingRight: '12px',
                        height: '42px',
                        borderRadius: '10px',
                        border: '1.5px solid #cbd5e1',
                        backgroundColor: '#f8fafc',
                        fontSize: '0.88rem'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Badge bg="light" text="dark" className="border px-3 py-2 fw-semibold" style={{ fontSize: '0.85rem', borderRadius: '8px' }}>
                    <i className="mdi mdi-image-outline text-primary me-1"></i>
                    {filteredPhotos.length} {filteredPhotos.length === 1 ? t('image_available', 'imagen disponible') : t('images_available', 'imágenes disponibles')}
                  </Badge>
                </div>
              </div>

              {/* Dynamic Loading State */}
              {loading ? (
                <LoadingState
                  size="lg"
                  title={t('loading_gallery', 'Cargando galería multimedia...')}
                  description={t('loading_gallery_desc', 'Consultando imágenes del almacenamiento central Rutex')}
                />
              ) : filteredPhotos.length === 0 ? (
                <div className="text-center py-5 my-4">
                  <div className="mb-3 d-inline-flex p-4 rounded-circle" style={{ backgroundColor: '#f1f5f9' }}>
                    <i className="mdi mdi-image-off" style={{ fontSize: '48px', color: '#94a3b8' }}></i>
                  </div>
                  <h5 className="fw-bold text-dark mb-1">{t('no_images_found', 'No se encontraron imágenes en la galería.')}</h5>
                  <p className="text-muted small mb-3">
                    {search ? t('no_images_search_match', 'Ninguna imagen coincide con el término de búsqueda.') : t('no_images_in_server', 'Aún no hay imágenes en el servidor central.')}
                  </p>
                  <Button 
                    variant="outline-primary"
                    style={{ borderRadius: '8px', fontWeight: 'bold' }}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <i className="mdi mdi-upload me-1"></i> {t('btn_upload_now', 'Subir imágenes ahora')}
                  </Button>
                </div>
              ) : (
                /* Uniform Responsive Card Grid */
                <Row className="g-3">
                  {filteredPhotos.map((photo, idx) => {
                    const fileName = photo.originalSrc ? photo.originalSrc.split('/').pop() : photo.src.split('/').pop();
                    const isDeleting = deletingSrc === photo.originalSrc;

                    return (
                      <Col xs={6} sm={4} md={3} lg={2} key={idx}>
                        <Card 
                          className="h-100 shadow-sm border-1"
                          style={{ 
                            borderRadius: '12px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff',
                            borderColor: '#e2e8f0',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onClick={() => openLightBox(idx)}
                          onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.borderColor = '#183eeb';
                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 62, 235, 0.16)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.boxShadow = '';
                          }}
                        >
                          {/* Image Box */}
                          <div style={{ height: '140px', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', position: 'relative', borderBottom: '1px solid #f1f5f9' }}>
                            <img 
                              src={photo.src} 
                              alt={fileName} 
                              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            
                            {/* Action Buttons Overlay */}
                            <div className="position-absolute top-0 end-0 m-2 d-flex gap-1">
                              <Button 
                                size="sm" 
                                variant="danger" 
                                style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)' }} 
                                onClick={(e) => handleDelete(photo.originalSrc, e)}
                                disabled={isDeleting}
                                title={t('delete_from_server', 'Eliminar del Servidor')}
                              >
                                {isDeleting ? <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} /> : <i className="mdi mdi-delete" style={{ fontSize: '13px' }}></i>}
                              </Button>
                            </div>
                          </div>

                          {/* Footer with file title */}
                          <Card.Footer className="p-2 text-center bg-white border-0">
                            <small className="text-truncate d-block fw-semibold text-dark" title={fileName} style={{ fontSize: '0.78rem' }}>
                              {fileName}
                            </small>
                          </Card.Footer>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Lightbox Modal */}
      <Lightbox 
        open={isOpen}
        close={() => setIsOpen(false)}
        slides={filteredPhotos.map(image => ({ src: image.src }))}
        index={index}
        plugins={[Fullscreen, Slideshow, Thumbnails, Zoom]}
      />
    </>
  );
}

export default Gallery;